/**
 * Перенос `lib/src/presentation/providers/client_provider.dart` вместе с
 * той частью dio, которой пользовалось приложение.
 *
 * Dio в вебе не нужен: в браузере есть `fetch`. Сохранены три вещи, на
 * которых держится остальной код: набор методов (`get`/`post` с
 * `queryParameters`, `headers`, телом и таймаутами), порядок перехватчиков
 * и форма ошибки — [HttpError], которую разбирает `AppException`.
 *
 * Отличие от dio одно, и оно вынужденное: `fetch` не сообщает, когда
 * соединение установлено и когда тело запроса ушло. Как это отражено на
 * таймаутах — см. [uploadDeadline].
 */

import {
  HTTP_ERROR_NAME,
  HttpErrorType,
  isHttpError,
  type HttpError,
} from '@/utils/exceptions/appException';
import { DiagnosticsInterceptor } from '@/utils/http/interceptors/diagnosticsInterceptor';
import { LoggerInterceptor } from '@/utils/http/interceptors/loggerInterceptor';
import { PathInterceptor } from '@/utils/http/interceptors/pathInterceptor';
import type {
  HttpInterceptor,
  HttpMethod,
  HttpPostOptions,
  HttpRequest,
  HttpRequestOptions,
  HttpResponse,
  HttpTimeouts,
} from '@/utils/http/types';
import { requestUri } from '@/utils/http/types';
import { toRequestUrl } from '@/utils/proxyUrl';

/** `BaseOptions(connectTimeout: Duration(seconds: 10), …)` из Dart. */
const DEFAULT_CONNECT_TIMEOUT_MS = 10_000;
const DEFAULT_RECEIVE_TIMEOUT_MS = 10_000;
const DEFAULT_SEND_TIMEOUT_MS = 10_000;

/** Границы «успешного» ответа: `status < 200 || status >= 300` — ошибка. */
const HTTP_OK_MIN = 200;
const HTTP_OK_MAX_EXCLUSIVE = 300;

/** `Headers.jsonContentType` из dio. */
const JSON_CONTENT_TYPE = 'application/json';

/** Начало тела, по которому его пробуют разобрать как JSON. */
const JSON_BODY_START = /^[[{]/;

/** Всё, что нужно одному обмену: собирается в `#exchange`. */
interface Exchange {
  /** Адрес для `fetch` — уже завёрнутый в прокси. */
  readonly requestUrl: string;
  /** Настоящий адрес — для журнала и ошибок. */
  readonly uri: string;
  readonly request: HttpRequest;
  readonly body: BodyInit | undefined;
  readonly abort: AbortController;
  readonly deadline: RequestDeadline;
}

export interface HttpClientInit {
  readonly connectTimeout?: number;
  readonly receiveTimeout?: number;
  readonly sendTimeout?: number;
  readonly interceptors?: readonly HttpInterceptor[];
}

/**
 * Один этап запроса под таймером.
 *
 * `fetch` отменяется только целиком, через `AbortController`, поэтому по
 * самой отмене не понять, какой таймер сработал. Этап это помнит — и
 * причина сбоя получается той же, что выставил бы dio.
 */
class RequestDeadline {
  constructor(abort: AbortController) {
    this.#abort = abort;
  }

  readonly #abort: AbortController;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #firedType: HttpErrorType | null = null;

  /** Причина, по которой запрос отменён таймером, либо `null`. */
  get firedType(): HttpErrorType | null {
    return this.#firedType;
  }

  arm(ms: number, type: HttpErrorType): void {
    this.disarm();
    this.#firedType = null;
    this.#timer = setTimeout(() => {
      this.#firedType = type;
      this.#abort.abort();
    }, ms);
  }

  disarm(): void {
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
  }
}

/** Отмена запроса: и таймером, и по `close()` она приходит одинаково. */
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Бюджет на всё, что происходит до заголовков ответа, и причина сбоя.
 *
 * Dio делил это время надвое: `connectTimeout` на установку соединения и
 * `sendTimeout` на запись тела. `fetch` момента установки не показывает,
 * поэтому этап один. Когда тело есть и на его отправку выделено больше
 * (распознавание речи шлёт wav с `sendTimeout` 60 с при `connectTimeout`
 * 10 с), берётся больший бюджет и причина `sendTimeout`; иначе —
 * `connectTimeout`, как у обычного запроса к недоступному серверу.
 */
export function uploadDeadline(
  timeouts: HttpTimeouts,
  hasBody: boolean,
): { readonly ms: number; readonly type: HttpErrorType } {
  if (hasBody && timeouts.sendTimeout > timeouts.connectTimeout) {
    return { ms: timeouts.sendTimeout, type: HttpErrorType.sendTimeout };
  }
  return { ms: timeouts.connectTimeout, type: HttpErrorType.connectionTimeout };
}

/** Тело для `fetch`: multipart уходит как есть, остальное — JSON-ом. */
function buildBody(data: unknown): BodyInit | undefined {
  if (data === null || data === undefined) return undefined;
  if (data instanceof FormData) return data;
  if (typeof data === 'string') return data;
  return JSON.stringify(data);
}

/**
 * Заголовки запроса. Для `FormData` `Content-Type` не ставится намеренно:
 * границу (boundary) проставляет сам браузер, и вручную её не собрать.
 */
function buildHeaders(
  headers: Readonly<Record<string, string>>,
  data: unknown,
): Record<string, string> {
  const needsJson = data !== null && data !== undefined && !(data instanceof FormData);
  return {
    ...(needsJson ? { 'Content-Type': JSON_CONTENT_TYPE } : {}),
    ...headers,
  };
}

/**
 * Тело ответа: JSON, если так говорит `Content-Type` или само тело.
 *
 * Неразобранное тело ошибкой не считается — сырой текст уходит дальше как
 * есть: у ответа 502 от прокси тело вообще html, а показать его в журнале
 * и в сообщении сбоя надо.
 */
export async function readResponseData(response: Response): Promise<unknown> {
  const text = await response.text();
  // Пустое тело в dio с `ResponseType.json` давало `null`.
  if (text.length === 0) return null;
  const contentType = response.headers.get('content-type') ?? '';
  const looksJson =
    contentType.toLowerCase().includes('json') || JSON_BODY_START.test(text.trimStart());
  if (!looksJson) return text;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function transportError(error: unknown, deadline: RequestDeadline, uri: string): HttpError {
  const firedType = deadline.firedType;
  if (firedType !== null) {
    return { name: HTTP_ERROR_NAME, type: firedType, requestUrl: uri, cause: error };
  }
  // Отмена не по таймеру: своей отмены у клиента нет, но форма dio её знала.
  if (isAbortError(error)) {
    return { name: HTTP_ERROR_NAME, type: HttpErrorType.cancel, requestUrl: uri, cause: error };
  }
  // На отказ DNS, отказ в соединении, обрыв сети и запрет CORS браузер
  // отвечает одним `TypeError` — различить их нельзя.
  return {
    name: HTTP_ERROR_NAME,
    type: HttpErrorType.connectionError,
    requestUrl: uri,
    cause: error,
  };
}

/** Ответ вне 2xx — `DioExceptionType.badResponse` вместе с телом. */
function badResponseError(status: number, data: unknown, uri: string): HttpError {
  return {
    name: HTTP_ERROR_NAME,
    type: HttpErrorType.badResponse,
    statusCode: status,
    response: { status, data },
    requestUrl: uri,
  };
}

/**
 * HTTP-клиент поверх `fetch` — замена `Dio`.
 *
 * Базового адреса у клиента нет: путь к моменту отправки уже абсолютный,
 * его собирает `Endpoints` и доводит `PathInterceptor`.
 */
export class HttpClient {
  constructor(init: HttpClientInit = {}) {
    this.#timeouts = {
      connectTimeout: init.connectTimeout ?? DEFAULT_CONNECT_TIMEOUT_MS,
      receiveTimeout: init.receiveTimeout ?? DEFAULT_RECEIVE_TIMEOUT_MS,
      sendTimeout: init.sendTimeout ?? DEFAULT_SEND_TIMEOUT_MS,
    };
    this.#interceptors = init.interceptors ?? [];
  }

  readonly #timeouts: HttpTimeouts;
  readonly #interceptors: readonly HttpInterceptor[];

  get(path: string, options: HttpRequestOptions = {}): Promise<HttpResponse> {
    return this.#send('GET', path, options);
  }

  post(path: string, options: HttpPostOptions = {}): Promise<HttpResponse> {
    return this.#send('POST', path, options);
  }

  async #send(
    method: HttpMethod,
    path: string,
    options: HttpPostOptions,
  ): Promise<HttpResponse> {
    const request = this.#buildRequest(method, path, options);
    // Перехватчики запроса идут в порядке добавления — как в dio.
    const prepared = this.#interceptors.reduce(
      (current, interceptor) => interceptor.onRequest?.(current) ?? current,
      request,
    );

    try {
      const response = await this.#exchange(prepared);
      return this.#interceptors.reduce(
        (current, interceptor) => interceptor.onResponse?.(current) ?? current,
        response,
      );
    } catch (e) {
      const error = asHttpError(e, prepared);
      throw this.#interceptors.reduce(
        (current, interceptor) => interceptor.onError?.(current, prepared) ?? current,
        error,
      );
    }
  }

  #buildRequest(method: HttpMethod, path: string, options: HttpPostOptions): HttpRequest {
    return {
      method,
      path,
      queryParameters: options.queryParameters ?? {},
      headers: options.headers ?? {},
      data: options.data ?? null,
      timeouts: {
        connectTimeout: options.connectTimeout ?? this.#timeouts.connectTimeout,
        receiveTimeout: options.receiveTimeout ?? this.#timeouts.receiveTimeout,
        sendTimeout: options.sendTimeout ?? this.#timeouts.sendTimeout,
      },
      extra: {},
    };
  }

  /** Сам обмен: отправка, ожидание заголовков, чтение тела. */
  async #exchange(request: HttpRequest): Promise<HttpResponse> {
    const uri = requestUri(request);
    // Через прокси уходит завёрнутый адрес; наружу — настоящий.
    const requestUrl = toRequestUrl(uri);
    const abort = new AbortController();
    const deadline = new RequestDeadline(abort);
    const body = buildBody(request.data);

    const response = await this.#fetchHeaders({ requestUrl, uri, request, body, abort, deadline });
    const data = await this.#readBody(response, request, deadline, uri);

    if (response.status < HTTP_OK_MIN || response.status >= HTTP_OK_MAX_EXCLUSIVE) {
      throw badResponseError(response.status, data, uri);
    }

    return {
      data,
      statusCode: response.status,
      headers: response.headers,
      requestUrl: uri,
      request,
    };
  }

  async #fetchHeaders(exchange: Exchange): Promise<Response> {
    const { requestUrl, uri, request, body, abort, deadline } = exchange;
    const init: RequestInit = {
      method: request.method,
      headers: buildHeaders(request.headers, request.data),
      body,
      signal: abort.signal,
      cache: 'no-store',
    };
    const upload = uploadDeadline(request.timeouts, body !== undefined);
    deadline.arm(upload.ms, upload.type);
    try {
      return await fetch(requestUrl, init);
    } catch (e) {
      throw transportError(e, deadline, uri);
    } finally {
      deadline.disarm();
    }
  }

  async #readBody(
    response: Response,
    request: HttpRequest,
    deadline: RequestDeadline,
    uri: string,
  ): Promise<unknown> {
    deadline.arm(request.timeouts.receiveTimeout, HttpErrorType.receiveTimeout);
    try {
      return await readResponseData(response);
    } catch (e) {
      throw transportError(e, deadline, uri);
    } finally {
      deadline.disarm();
    }
  }
}

/**
 * Всё, что вылетело из обмена, приводится к [HttpError]: слой исключений
 * умеет разбирать только его. Чужая ошибка (например, наш же промах в
 * коде) не теряется — она остаётся причиной.
 */
function asHttpError(error: unknown, request: HttpRequest): HttpError {
  if (isHttpError(error)) return error;
  return {
    name: HTTP_ERROR_NAME,
    type: HttpErrorType.unknown,
    requestUrl: requestUri(request),
    cause: error,
  };
}

/**
 * `getBaseClient()` из Dart: новый клиент на каждый вызов, те же таймауты
 * и тот же порядок перехватчиков.
 */
export function getBaseClient(): HttpClient {
  return new HttpClient({
    connectTimeout: DEFAULT_CONNECT_TIMEOUT_MS,
    receiveTimeout: DEFAULT_RECEIVE_TIMEOUT_MS,
    sendTimeout: DEFAULT_SEND_TIMEOUT_MS,
    interceptors: [
      new PathInterceptor(),
      // После PathInterceptor — в журнал попадает уже настоящий адрес.
      new DiagnosticsInterceptor(),
      // `kDebugMode` — отладочный лог только в режиме разработки.
      ...(import.meta.env.DEV ? [new LoggerInterceptor()] : []),
    ],
  });
}

/** Аналог `clientProvider`: один общий клиент на все репозитории. */
export const httpClient: HttpClient = getBaseClient();
